import {
  getPointSeriesMaxSecs,
  normaliseTimeValuePoints,
  readArray,
  readFiniteNumber,
  toTimeValuePoints,
} from "./viewTimeSeries";
import { hasFirestoreFlowLoops as hasAnyFirestoreFlowLoops } from "./flowLoops";

function firstFiniteNumber(...values) {
  for (const value of values) {
    const numericValue = readFiniteNumber(value);
    if (numericValue != null) return numericValue;
  }

  return null;
}

function pickTraceTimes(processed, sampleCount) {
  const candidates = [
    processed?.resampledTimesSeconds,
    processed?.timesSeconds,
    processed?.timeSeconds,
    processed?.time_seconds,
  ];

  return (
    candidates.find(
      (candidate) => Array.isArray(candidate) && candidate.length === sampleCount,
    ) || []
  );
}

export function pickHeartDxPeakIntervals(docData) {
  return readArray(
    // Clean heartDx contract stores the RR series once under poincare.rr;
    // fall back to the legacy peak_intervals locations for older docs.
    docData?.results?.heartdx?.data?.poincare?.rr ??
      docData?.results?.heartdx?.data?.peak_intervals ??
      docData?.heartdx?.peak_intervals ??
      docData?.heartdx?.data?.peak_intervals,
  ).filter((value) => typeof value === "number" && Number.isFinite(value));
}

export function buildCardiopulmonaryAdditionalMetricsRecord(docData) {
  const mpm = docData?.results?.mpm ?? docData?.resp?.results?.mpm ?? null;
  const respiratoryRate = mpm?.respiratory_rate ?? null;
  const respiratoryRateMetrics = respiratoryRate?.metrics ?? null;
  const flowLoopSummary = mpm?.flow_loop?.summary_metrics ?? null;

  return {
    meanExhaleInhaleAreaRatio: firstFiniteNumber(
      flowLoopSummary?.meanExhaleInhaleAreaRatio,
    ),
    rr_min: firstFiniteNumber(
      mpm?.rr_min,
      respiratoryRate?.minBreathsPerMinute,
      respiratoryRate?.rr_min,
      respiratoryRateMetrics?.minBreathsPerMinute,
    ),
    rr_max: firstFiniteNumber(
      mpm?.rr_max,
      respiratoryRate?.maxBreathsPerMinute,
      respiratoryRate?.rr_max,
      respiratoryRateMetrics?.maxBreathsPerMinute,
    ),
    rr_mean: firstFiniteNumber(
      respiratoryRate?.meanBreathsPerMinute,
      respiratoryRateMetrics?.meanBreathsPerMinute,
      mpm?.rr_mean,
      respiratoryRate?.averageBreathsPerMinute,
      mpm?.rr_avg,
    ),
    rr_median: firstFiniteNumber(
      respiratoryRate?.medianBreathsPerMinute,
      respiratoryRateMetrics?.medianBreathsPerMinute,
      mpm?.rr_median,
    ),
    min_breath_duration_seconds: firstFiniteNumber(
      flowLoopSummary?.minBreathDurationSeconds,
    ),
    max_breath_duration_seconds: firstFiniteNumber(
      flowLoopSummary?.maxBreathDurationSeconds,
    ),
    mean_breath_duration_seconds: firstFiniteNumber(
      flowLoopSummary?.meanBreathDurationSeconds,
      respiratoryRateMetrics?.intervalMeanSeconds,
    ),
    breath_count: firstFiniteNumber(
      respiratoryRate?.breathCount,
      mpm?.flow_loop?.breath_count,
    ),
  };
}

export function buildPoincareFromIntervals(intervals) {
  if (!Array.isArray(intervals) || intervals.length < 2) {
    return { x: [], y: [] };
  }

  const x = [];
  const y = [];

  for (let index = 0; index < intervals.length - 1; index += 1) {
    const current = intervals[index];
    const next = intervals[index + 1];

    if (
      typeof current === "number" &&
      Number.isFinite(current) &&
      typeof next === "number" &&
      Number.isFinite(next)
    ) {
      x.push(current);
      y.push(next);
    }
  }

  return { x, y };
}

export function pickCardiopulmonaryDetrendedSignal(docData) {
  const processed =
    docData?.["mpm-signal-processed"] ||
    docData?.mpmSignalProcessed ||
    docData?.mpm_signal_processed ||
    null;

  if (!processed) {
    return {
      points: [],
      maxSecs: 60,
    };
  }

  const values = readArray(
    processed.detrendedTrace ??
      processed.detrendedSignal ??
      processed.detrended_signal,
  );

  const times = readArray(pickTraceTimes(processed, values.length));

  const samplingRateHz =
    readFiniteNumber(processed.samplingRateHz) ??
    readFiniteNumber(processed.sampling_rate_hz) ??
    30;

  const points = toTimeValuePoints(values, times, samplingRateHz);
  const normalisedPoints = normaliseTimeValuePoints(points);

  return {
    points: normalisedPoints,
    maxSecs: normalisedPoints.length
      ? getPointSeriesMaxSecs(normalisedPoints, 60)
      : 60,
  };
}

export function hasFirestoreFlowLoops(docData) {
  return hasAnyFirestoreFlowLoops(docData);
}
